import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Zap, Shield, Wifi, Monitor, Users, Settings } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

const FeaturesAndBenefits1Gb = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: <Zap className="h-8 w-8 text-[#069E50]" />,
      title: "High Performance",
      description: "Delivers reliable 1Gb AV over IP transmission with ultra-low latency for professional applications"
    },
    {
      icon: <Shield className="h-8 w-8 text-[#069E50]" />,
      title: "Enterprise-Grade Reliability",
      description: "Built for mission-critical environments with robust hardware and proven technology"
    },
    {
      icon: <Wifi className="h-8 w-8 text-[#069E50]" />,
      title: "Network Efficiency",
      description: "Optimized for standard 1Gb network infrastructure, maximizing existing investment"
    },
    {
      icon: <Monitor className="h-8 w-8 text-[#069E50]" />,
      title: "4K Video Support",
      description: "Supports 4K@60Hz video transmission with visually lossless compression"
    },
    {
      icon: <Users className="h-8 w-8 text-[#069E50]" />,
      title: "Scalable Solutions",
      description: "Easily expandable from small installations to large-scale enterprise deployments"
    },
    {
      icon: <Settings className="h-8 w-8 text-[#069E50]" />,
      title: "Customizable Interface",
      description: "Unit features a customizable window where you can add your company logo or customer's logo for professional branding"
    }
  ];

  const productFeatures = [
    {
      title: "Test Pattern Generator (TPG)",
      items: [
        "Built-in tool to test system integrity without a source.",
        "Identifies HDMI input, source, or cable issues.",
        "Confirms IP transport (cable run) functionality."
      ]
    },
    {
      title: "HDMI Tester",
      items: [
        "Validates HDMI connection from decoder to monitor via Aurora Dash AV software."
      ]
    },
    {
      title: "Rapid Deployment via Dash AV",
      items: [
        "Auto-discovers all endpoints on the network.",
        "Editable via Excel for easy configuration.",
        "Enables site-wide setup in minutes.",
        "Rapid deployment function allows installers to configure multiple units in a matter of minutes."
      ]
    },
    {
      title: "Customizable Window with Logo Support",
      items: [
        "Unit features a customizable window for professional branding.",
        "Add your company logo or customer's logo to the interface.",
        "Enhances brand presence and professional appearance."
      ]
    },
    {
      title: "Dual Network Ports (Copper & Fiber)",
      items: [
        "SFP port allows seamless switching between copper and fibre.",
        "Direct PC access with RJ45 SFP module—no need to access rack."
      ]
    },
    {
      title: "Quick IP Range Detection",
      items: [
        "IP settings viewable via unit's front menu—saves time during setup."
      ]
    },
    {
      title: "24/7 System Monitoring",
      items: [
        "Continuous logging of system events.",
        "Rapid troubleshooting and remote diagnostics."
      ]
    },
    {
      title: "Self-Healing & Auto-Interoperability",
      items: [
        "Automatically adapts to incompatible third-party devices.",
        "Logs issues and attempts to maintain functionality."
      ]
    },
    {
      title: "Electronic Programming Guide (EPG)",
      items: [
        "Built-in channel guide with channel switching—mimics TV experience.",
        "Supports PIP (Picture-in-Picture) and remote control (IRC-BT1).",
        "Supports I/R and RF transmissions.",
        "Compatible with control systems like Crestron, AMX, QSC.",
        "MJPEG Preview 480p 15 frames for LT and 720p 30 frames for Pro Version."
      ]
    },
    {
      title: "Auracast-Ready (Bluetooth LE)",
      items: [
        "Supports long-range wireless broadcast audio to multiple devices.",
        "Future-ready, upgradeable, and supported by major tech brands.",
        "Low-cost add-on (BT-AC1) with widespread industry support."
      ]
    },
    {
      title: "Low Power Consumption",
      items: [
        "Uses as little as 6.2 watts, versus 18–32W in competitors.",
        "Can Save up to £150 per endpoint per year—massive long-term savings."
      ]
    },
    {
      title: "Power over Ethernet (PoE)",
      items: [
        "Simplifies installations—single Cat cable per unit.",
        "Reduces rack clutter and cost."
      ]
    },
    {
      title: "Unified SFP Port Design",
      items: [
        "One SKU supports copper & fibre.",
        "SFP port convertible to LAN for flexibility."
      ]
    },
    {
      title: "USA-Based Manufacturing",
      items: [
        "Designed and built in New Jersey.",
        "Greater control, reliability, and innovation.",
        "Consistent supply even during global shortages."
      ]
    },
    {
      title: "Expert Tech Support",
      items: [
        "Direct support from product developers.",
        "Friendly, knowledgeable, and customer-focused team."
      ]
    }
  ];

  const benefits = [
    "Cost-effective deployment on existing 1Gb network infrastructure",
    "Reduced cable complexity with single network cable for AV and control",
    "Centralized management and monitoring capabilities",
    "Future-proof technology with software-based feature updates",
    "Seamless integration with existing IT networks",
    "Lower total cost of ownership compared to traditional matrix solutions"
  ];

  const handleViewProducts = () => {
    // Scroll to top when navigating
    window.scrollTo(0, 0);
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-8">
        <Link
          to="/avoip-options"
          className="mb-8 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back.to.avoip')}
        </Link>
        <h1 className="mb-4 text-4xl font-bold tracking-tight">
          VPX 1Gb Solutions - Features & Benefits
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl">
          Discover the powerful features and compelling benefits of Aurora's VPX 1Gb AV over IP solutions, 
          designed to deliver professional-grade performance while maximizing your network investment.
        </p>
      </div>

      {/* Key Features Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Key Features</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Product Features & Technical Capabilities Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Product Features & Technical Capabilities</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {productFeatures.map((feature, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{feature.title}</h3>
              <ul className="space-y-2">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Business Benefits</h2>
        <div className="bg-card rounded-lg p-8 border shadow-sm">
          <div className="grid gap-4 md:grid-cols-2">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#069E50] mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center bg-muted rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-4">Ready to Explore 1Gb Products?</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          Browse our complete range of 1Gb AV over IP solutions and find the perfect products for your installation.
        </p>
        <Link to="/group/avoip?speed=1gb" onClick={handleViewProducts}>
          <Button size="lg">
            View 1Gb Products
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default FeaturesAndBenefits1Gb;
